// canvasOfflineQueue.ts — Ship 3 follow-up (May 2, 2026)
//
// Tiny offline queue for canvas_visits inserts. Solves the failure mode where a
// canvasser taps an outcome on a porch with weak signal, the network round-trip
// fails or times out, and the visit silently doesn't land. Without this, the
// only way to know is when the audit board mismatches reality.
//
// Design:
//   - Client generates a UUID at tap time (client_uuid).
//   - Try the supabase upsert immediately.
//   - On any network/transient error, queue the payload in IndexedDB.
//   - Background flush every 30s + on `window.online` event.
//   - Idempotent: upsert(..., onConflict='client_uuid', ignoreDuplicates=true)
//     so a retry of a visit that already landed is harmlessly skipped.
//
// Scope intentionally minimal — see Greg's "yes" Apr/May 2026:
//   - "Queue and retry while the tab is open"
//   - NOT a full offline-first PWA (no service worker, no app-shell caching)
//   - If the rep closes the tab with items still queued, the queue persists in
//     IndexedDB and flushes when the tab reopens. Browser-killing iOS Safari
//     does NOT lose the queue (IndexedDB survives tab close).

import type { SupabaseClient } from "@supabase/supabase-js";

const DB_NAME = "bardin-canvas-offline";
const DB_VERSION = 1;
const STORE = "queued-visits";

// ── IndexedDB wrapper ───────────────────────────────────────────────────
let _dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "client_uuid" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const store = t.objectStore(STORE);
        const result = fn(store);
        t.oncomplete = () => {
          if (result instanceof IDBRequest) resolve(result.result as T);
          else (result as Promise<T>).then(resolve, reject);
        };
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

// ── Visit payload shape (keep aligned with canvas_visits columns) ────────
export interface VisitPayload {
  client_uuid: string;
  knock_pin_id: string;
  visited_by: string;
  outcome: "no_answer" | "contact_made" | "rent" | "appt_set" | "follow_up" | "wrong_address" | "do_not_knock";
  notes?: string | null;
  appointment_at?: string | null;
  lat?: number | null;
  lon?: number | null;
  voice_note_url?: string | null;
  photo_storage_path?: string | null;
  // Locally tracked metadata (NOT inserted into DB)
  _queued_at: string;
  _attempts: number;
  _last_error?: string;
}

// ── Public API ──────────────────────────────────────────────────────────

export function newClientUuid(): string {
  // Browsers without crypto.randomUUID (rare on modern phones) get a fallback
  if (typeof crypto !== "undefined" && (crypto as any).randomUUID) {
    return (crypto as any).randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Try to submit a visit. If the network is up, the insert lands immediately
 * and resolves with `{ ok: true, queued: false }`. If the network is dead or
 * the request errors out for transient reasons, the payload is queued in
 * IndexedDB and the function still resolves with `{ ok: true, queued: true }`
 * so the UI doesn't block. Background flush will retry.
 */
export async function submitVisit(
  supabase: SupabaseClient,
  payload: Omit<VisitPayload, "_queued_at" | "_attempts" | "_last_error">,
): Promise<{ ok: true; queued: boolean }> {
  // Strip local-only fields before inserting
  const { client_uuid, knock_pin_id, visited_by, outcome, notes, appointment_at, lat, lon, voice_note_url, photo_storage_path } = payload;
  const insertable = {
    client_uuid, knock_pin_id, visited_by, outcome,
    notes: notes ?? null,
    appointment_at: appointment_at ?? null,
    lat: lat ?? null,
    lon: lon ?? null,
    voice_note_url: voice_note_url ?? null,
    photo_storage_path: photo_storage_path ?? null,
  };

  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  if (!online) {
    await enqueue(payload);
    return { ok: true, queued: true };
  }

  try {
    const { error } = await supabase
      .from("canvas_visits")
      .upsert(insertable, { onConflict: "client_uuid", ignoreDuplicates: true });
    if (error) {
      // Treat as transient — queue it. Persistent errors (RLS, FK violation)
      // would be retried but never succeed; that's acceptable for v1 since the
      // queue UI lets the rep see and clear stuck items.
      await enqueue({ ...payload, _last_error: error.message });
      return { ok: true, queued: true };
    }
    return { ok: true, queued: false };
  } catch (e: any) {
    // True network error — fetch threw
    await enqueue({ ...payload, _last_error: e?.message ?? String(e) });
    return { ok: true, queued: true };
  }
}

async function enqueue(
  payload: Omit<VisitPayload, "_queued_at" | "_attempts">
    & Partial<Pick<VisitPayload, "_attempts" | "_queued_at" | "_last_error">>,
): Promise<void> {
  const row: VisitPayload = {
    ...payload,
    _queued_at: payload._queued_at ?? new Date().toISOString(),
    _attempts: (payload._attempts ?? 0) + 1,
  };
  await tx("readwrite", (store) => store.put(row));
}

export async function listQueued(): Promise<VisitPayload[]> {
  return tx<VisitPayload[]>("readonly", (store) => store.getAll() as IDBRequest<VisitPayload[]>);
}

export async function queueCount(): Promise<number> {
  return tx<number>("readonly", (store) => store.count() as IDBRequest<number>);
}

export async function clearQueue(): Promise<void> {
  await tx<undefined>("readwrite", (store) => store.clear() as IDBRequest<undefined>);
}

async function deleteQueued(client_uuid: string): Promise<void> {
  await tx<undefined>("readwrite", (store) => store.delete(client_uuid) as IDBRequest<undefined>);
}

/**
 * Drain the queue. Each item gets one retry; on success it's removed, on
 * failure it stays for the next flush.
 */
export async function flushQueue(
  supabase: SupabaseClient,
): Promise<{ flushed: number; remaining: number; errors: string[] }> {
  const items = await listQueued();
  if (items.length === 0) return { flushed: 0, remaining: 0, errors: [] };

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { flushed: 0, remaining: items.length, errors: ["offline"] };
  }

  let flushed = 0;
  const errors: string[] = [];
  for (const v of items) {
    const insertable = {
      client_uuid: v.client_uuid,
      knock_pin_id: v.knock_pin_id,
      visited_by: v.visited_by,
      outcome: v.outcome,
      notes: v.notes ?? null,
      appointment_at: v.appointment_at ?? null,
      lat: v.lat ?? null,
      lon: v.lon ?? null,
      voice_note_url: v.voice_note_url ?? null,
      photo_storage_path: v.photo_storage_path ?? null,
    };
    try {
      const { error } = await supabase
        .from("canvas_visits")
        .upsert(insertable, { onConflict: "client_uuid", ignoreDuplicates: true });
      if (error) {
        errors.push(`${v.client_uuid.slice(0, 8)}: ${error.message}`);
        // Bump attempt count
        await enqueue({ ...v, _last_error: error.message });
      } else {
        await deleteQueued(v.client_uuid);
        flushed++;
      }
    } catch (e: any) {
      errors.push(`${v.client_uuid.slice(0, 8)}: ${e?.message ?? e}`);
      await enqueue({ ...v, _last_error: e?.message ?? String(e) });
    }
  }

  const remaining = (await listQueued()).length;
  return { flushed, remaining, errors };
}

/**
 * Start a background flush worker. Returns a cleanup function.
 *   - Flushes every 30 seconds while the tab is open
 *   - Flushes immediately when the browser fires `online` event
 *   - Calls onChange(count) after each flush so the UI can update its pill
 */
export function startBackgroundSync(
  supabase: SupabaseClient,
  onChange: (count: number) => void,
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  async function tick() {
    if (stopped) return;
    try {
      const result = await flushQueue(supabase);
      onChange(result.remaining);
    } catch {
      // swallow — UI keeps working
    }
  }

  // Initial probe
  queueCount().then((n) => onChange(n)).catch(() => {});
  // Try one flush immediately in case the page just loaded with a queue
  tick();

  timer = setInterval(tick, 30_000);
  const onOnline = () => tick();
  if (typeof window !== "undefined") {
    window.addEventListener("online", onOnline);
  }

  return () => {
    stopped = true;
    if (timer) clearInterval(timer);
    if (typeof window !== "undefined") {
      window.removeEventListener("online", onOnline);
    }
  };
}
